/**
 * chartScale — axis abbreviation, tick selection and bar-label placement.
 *
 * WS-VIEW-3. The workspace's axis formatter only knew two tiers, M and K, so
 * anything at billion scale was still divided by 1e6: a $100bn figure formatted
 * as "$100000.0M", overflowed the 56px axis gutter, and the clipped remains read
 * as a column of identical "00000.0M" ticks. Value labels had the same problem
 * horizontally, and a short bar (EBITDA against a revenue-scale axis) put its
 * label hard against the category axis, on top of the "EBITDA" tick text.
 *
 * The geometry lives here as pure functions rather than inline in the chart
 * because it cannot be tested through the chart: ResponsiveContainer measures
 * 0x0 under jsdom, so nothing renders and no coordinate can be asserted. Pure
 * in, pure out, tested directly.
 *
 * Nothing here touches financial values — it only decides how an already-derived
 * number is *drawn*. Rounding for display never feeds back into a figure.
 */

/** Average glyph advance as a fraction of font size, for tabular digits plus a
 *  currency symbol. Deliberately generous: over-estimating width hides a label
 *  that might have fitted, under-estimating lets two labels collide. */
const GLYPH_RATIO = 0.62

/**
 * Magnitudes above this are almost certainly a typo (a stray extra digit), not a
 * real figure — no SMB engagement has a trillion-unit line item in any currency.
 * The threshold is set that high on purpose: the app supports weak and
 * zero-decimal currencies where a legitimately large number is routine, so a
 * tighter bound would cry wolf on a perfectly valid JPY or IDR deal.
 */
export const IMPLAUSIBLE_MAGNITUDE = 1e12

export const isImplausibleMagnitude = (val: unknown): boolean =>
  typeof val === 'number' && Number.isFinite(val) && Math.abs(val) >= IMPLAUSIBLE_MAGNITUDE

/**
 * Round half-up on the *decimal* value, not on its binary representation.
 *
 * `(2.15).toFixed(1)` is "2.1", because the nearest double to 2.15 sits a hair
 * below it. Left alone, the axis rounds half-up at some magnitudes and half-down
 * at others with no pattern the reader can see. `toPrecision(15)` discards the
 * residue before the rounding decision, so 2.15 → 2.2 and 2.25 → 2.3, always.
 *
 * Display only — the underlying figure is never altered.
 */
const roundTo = (n: number, digits: number): number => {
  const factor = 10 ** digits
  return Math.round(Number((n * factor).toPrecision(15))) / factor
}

/**
 * Compact currency for an axis tick or a bar label: `$1.2M`, `-$450K`, `$1.0B`,
 * `$2.4T`. The sign leads, so a negative EBITDA cannot be misread as positive.
 *
 * Mantissas below 100 keep one decimal ("$1.2M" is more useful than "$1M");
 * at or above 100 the decimal is dropped, so no tick is ever longer than about
 * six characters and the axis gutter can be sized from the ticks themselves.
 */
export function abbreviateCurrency(val: number, symbol: string): string {
  if (!Number.isFinite(val)) return '—'
  const sign = val < 0 ? '-' : ''
  const abs = Math.abs(val)

  if (abs < 1_000) {
    // Small values keep their exact magnitude — abbreviating $840 to "$0.8K"
    // loses precision for no space saving.
    const rounded = Math.round(abs * 100) / 100
    return `${sign}${symbol}${rounded.toLocaleString('en-US', { maximumFractionDigits: 2 })}`
  }

  // Descending, so index i-1 is the next tier up.
  const tiers: Array<[number, string]> = [
    [1e12, 'T'],
    [1e9, 'B'],
    [1e6, 'M'],
    [1e3, 'K'],
  ]
  for (let i = 0; i < tiers.length; i++) {
    const [scale, suffix] = tiers[i]
    if (abs < scale) continue

    const mantissa = abs / scale
    const digits = mantissa >= 100 ? 0 : 1
    const rounded = roundTo(mantissa, digits)

    // Rounding can carry past the tier: 999,950,000 is "$1.0B", not "$1000M".
    if (rounded >= 1_000 && i > 0) {
      const [upScale, upSuffix] = tiers[i - 1]
      const upMantissa = abs / upScale
      const upDigits = upMantissa >= 100 ? 0 : 1
      return `${sign}${symbol}${roundTo(upMantissa, upDigits).toFixed(upDigits)}${upSuffix}`
    }
    return `${sign}${symbol}${rounded.toFixed(digits)}${suffix}`
  }
  // Unreachable: abs >= 1_000 always matches the K tier.
  return `${sign}${symbol}${abs}`
}

/** Estimated rendered width of a label, in px. */
export const estimateTextWidth = (text: string, fontSize: number): number =>
  text.length * fontSize * GLYPH_RATIO

// ── Ticks ────────────────────────────────────────────────────────────────────

/** Round a raw step up to the next 1 / 2 / 2.5 / 5 / 10 x 10^n. */
function niceStep(raw: number): number {
  const exp = Math.floor(Math.log10(raw))
  const pow = 10 ** exp
  const frac = raw / pow
  const nice = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 2.5 ? 2.5 : frac <= 5 ? 5 : 10
  return nice * pow
}

export interface NiceScale {
  domain: [number, number]
  ticks: number[]
  step: number
}

/**
 * A bar-chart Y scale with human tick steps.
 *
 * Zero is always included — a bar chart whose baseline is not zero exaggerates
 * differences — and the domain is widened to whole steps so the top tick is a
 * round number instead of the data maximum.
 *
 * Returns null when there is no meaningful scale to draw (no data, or every
 * value is zero); the caller should then leave the axis on its own defaults
 * rather than force a fabricated 0–1 range.
 */
export function niceTicks(
  values: readonly number[],
  targetCount = 5,
): NiceScale | null {
  const finite = values.filter(v => typeof v === 'number' && Number.isFinite(v))
  if (finite.length === 0) return null

  const lo = Math.min(0, ...finite)
  const hi = Math.max(0, ...finite)
  if (lo === hi) return null

  const step = niceStep((hi - lo) / Math.max(1, targetCount))
  // toPrecision(12) clears the 0.30000000000000004 class of artefact without
  // altering any value a human would notice.
  const round = (n: number) => Number(n.toPrecision(12))

  const dLo = round(Math.floor(lo / step) * step)
  const dHi = round(Math.ceil(hi / step) * step)

  const ticks: number[] = []
  // Hard stop at 40: a pathological domain must not spin here.
  for (let i = 0, t = dLo; t <= dHi + step / 2 && i < 40; i++, t = round(dLo + step * (i + 1))) {
    ticks.push(t)
  }

  return { domain: [dLo, dHi], ticks, step }
}

/**
 * Width for the Y-axis gutter, measured from the widest tick actually being
 * drawn. Hardcoding 56px was half the original defect: the axis could not grow
 * to fit "$100000.0M", so the text was clipped rather than shortened.
 */
export function axisWidthFor(
  ticks: readonly number[],
  format: (val: number) => string,
  fontSize = 10,
): number {
  const widest = ticks.reduce(
    (max, t) => Math.max(max, estimateTextWidth(format(t), fontSize)),
    0,
  )
  return Math.min(96, Math.max(44, Math.ceil(widest) + 12))
}

// ── Bar label placement ──────────────────────────────────────────────────────

export interface BarBox {
  /** Left edge of the bar. */
  x: number
  /** Top edge of the drawn rect (recharts normalises this — always the visual top). */
  y: number
  width: number
  height: number
}

export interface PlotBox {
  y: number
  height: number
}

export interface BarLabelPlacement {
  /** Horizontal centre of the bar. */
  x: number
  /** Text baseline. */
  y: number
  /** False when the label cannot be drawn without colliding with its neighbours. */
  visible: boolean
  /** True when the label had to be drawn inside the bar to stay in the plot. */
  inside: boolean
}

export interface PlaceBarLabelOptions {
  /** The plot area, if known. Without it, axis and ceiling clearance are skipped. */
  plot?: PlotBox | null
  /** Rendered label text, for the width check. */
  text: string
  fontSize?: number
  /** Horizontal space this label may occupy — the category slot, not the bar,
   *  since a narrow bar can still overhang into its own slot harmlessly. */
  slotWidth?: number
  /** Clearance above the bar. */
  gap?: number
  /** Band above the category axis that value labels must stay out of. This is
   *  what stops a short bar's label from landing on its "EBITDA" tick text. */
  axisClearance?: number
}

/**
 * Where to draw a bar's value label.
 *
 * Above the bar by default. Two clamps make it safe at any magnitude:
 *
 *  - a bar tall enough to reach the top of the plot would have its label
 *    clipped, so the label moves inside the bar's head instead;
 *  - a bar too short to lift its label clear of the category axis has the label
 *    pushed up to the top of the axis clearance band, leaving a visible gap
 *    between the value and the category name rather than printing one over the
 *    other.
 *
 * A label wider than its category slot is hidden — the exact figures are in the
 * table and the tooltip, so a legible chart beats a complete but unreadable one.
 */
export function placeBarLabel(
  box: BarBox,
  opts: PlaceBarLabelOptions,
): BarLabelPlacement {
  const fontSize = opts.fontSize ?? 10
  const gap = opts.gap ?? 6
  const axisClearance = opts.axisClearance ?? 6
  const x = box.x + box.width / 2

  const slotWidth = opts.slotWidth
  const visible =
    slotWidth == null || estimateTextWidth(opts.text, fontSize) <= slotWidth

  let y = box.y - gap
  let inside = false

  if (opts.plot) {
    const plotTop = opts.plot.y
    const plotBottom = opts.plot.y + opts.plot.height

    // Ceiling: the glyph box is (y - fontSize) .. y.
    if (y - fontSize < plotTop) {
      y = box.y + fontSize + gap
      inside = true
    }

    // Floor: never inside the band immediately above the category axis.
    const floor = plotBottom - axisClearance
    if (!inside && y > floor) y = floor
  }

  return { x, y, visible, inside }
}
